/**
 * Category Icon Mapping Utility
 * 
 * This provides a centralized way to map icon names (stored in DB) to:
 * 1. Lucide React icon components
 * 2. Emoji representations (for simple displays)
 * 3. Subcategory icon mappings
 * 
 * When adding new icons in the admin panel, also add them here.
 */

import {
  Package, Home, Car, Smartphone, Shirt, Sofa, Wrench, Briefcase, Anchor,
  Trees, Waves, Shield, Map, Crown, Zap, Droplets, HardHat, CarFront,
  Building2, Baby, Dumbbell, Palette, Music, Camera, BookOpen, Gem, Leaf,
  Bike, ShoppingBag, Utensils, Plane, Train, Bus, Truck, Tractor,
  Gamepad2, Monitor, Tv, Headphones, Watch, Glasses, Dog, Cat, Bird,
  Flower2, Hammer, Paintbrush, Scissors, Stethoscope, GraduationCap,
  Heart, Star, Coffee, Wine, Pizza, Cake, Gift, PartyPopper, Sparkles,
  Sun, Moon, Cloud, Umbrella, Thermometer, Wind, Mountain, Tent, Compass,
  Sailboat, Ship, PlaneTakeoff, CarTaxiFront, CircleDot, PaintBucket,
  Store, Building, Factory, Warehouse, School, Church, Hospital,
  Laptop, Printer, Router, Cpu, HardDrive, Database, Wifi,
  Phone, Mail, MessageSquare, Video, Image, FileText, Folder,
  Lock, Key, CreditCard, Wallet, PiggyBank, TrendingUp, BarChart,
  Users, UserPlus, UserCheck, Settings, Wrench as ToolIcon, Cog, Layers,
  // Additional icons for subcategories
  BedDouble, Armchair, Lamp, UtensilsCrossed, Refrigerator, Table2,
  MapPin, Clock, Briefcase as BriefcaseLucide, Award, Search, 
  Fish, ShoppingCart, Bed, Couch, TreePine, Activity,
  CircleDollarSign, CalendarCheck, GraduationCap as GradCap, Cuboid, Tag,
  Syringe, Megaphone, Bath, ChefHat
} from 'lucide-react';

// Map icon names to Lucide React components
export const iconComponentMap = {
  // Common categories
  'Package': Package,
  'Home': Home,
  'Car': Car,
  'Smartphone': Smartphone,
  'Shirt': Shirt,
  'Sofa': Sofa,
  'Wrench': Wrench,
  'Briefcase': Briefcase,
  'Anchor': Anchor,
  'Trees': Trees,
  'Waves': Waves,
  'Shield': Shield,
  'Map': Map,
  'Crown': Crown,
  'Zap': Zap,
  'Droplets': Droplets,
  'HardHat': HardHat,
  'CarFront': CarFront,
  'CarTaxiFront': CarTaxiFront,
  'PaintBucket': PaintBucket,
  
  // Buildings & Places
  'Building2': Building2,
  'Building': Building,
  'Store': Store,
  'Factory': Factory,
  'Warehouse': Warehouse,
  'School': School,
  'Church': Church,
  'Hospital': Hospital,
  
  // Vehicles & Transport
  'Bike': Bike,
  'Bus': Bus,
  'Truck': Truck,
  'Tractor': Tractor,
  'Train': Train,
  'Plane': Plane,
  'PlaneTakeoff': PlaneTakeoff,
  'Sailboat': Sailboat,
  'Ship': Ship,
  
  // Electronics & Tech
  'Monitor': Monitor,
  'Tv': Tv,
  'Laptop': Laptop,
  'Printer': Printer,
  'Router': Router,
  'Cpu': Cpu,
  'HardDrive': HardDrive,
  'Database': Database,
  'Wifi': Wifi,
  'Headphones': Headphones,
  'Gamepad2': Gamepad2,
  
  // Fashion & Accessories
  'Watch': Watch,
  'Glasses': Glasses,
  'Gem': Gem,
  'ShoppingBag': ShoppingBag,
  
  // People & Family
  'Baby': Baby,
  'Users': Users,
  'UserPlus': UserPlus,
  'UserCheck': UserCheck,
  
  // Sports & Fitness
  'Dumbbell': Dumbbell,
  
  // Arts & Entertainment
  'Palette': Palette,
  'Music': Music,
  'Camera': Camera,
  'Paintbrush': Paintbrush,
  
  // Education
  'BookOpen': BookOpen,
  'GraduationCap': GraduationCap,
  
  // Animals
  'Dog': Dog,
  'Cat': Cat,
  'Bird': Bird,
  
  // Nature & Outdoors
  'Leaf': Leaf,
  'Flower2': Flower2,
  'Mountain': Mountain,
  'Tent': Tent,
  'Compass': Compass,
  'Sun': Sun,
  'Moon': Moon,
  'Cloud': Cloud,
  'Umbrella': Umbrella,
  'Wind': Wind,
  
  // Food & Drink
  'Utensils': Utensils,
  'Coffee': Coffee,
  'Wine': Wine,
  'Pizza': Pizza,
  'Cake': Cake,
  
  // Tools & Work
  'Hammer': Hammer,
  'Scissors': Scissors,
  'Tool': ToolIcon,
  'Cog': Cog,
  'Settings': Settings,
  
  // Health
  'Stethoscope': Stethoscope,
  'Heart': Heart,
  'Thermometer': Thermometer,
  
  // Communication
  'Phone': Phone,
  'Mail': Mail,
  'MessageSquare': MessageSquare,
  'Video': Video,
  
  // Files & Documents
  'Image': Image,
  'FileText': FileText,
  'Folder': Folder,
  
  // Finance
  'CreditCard': CreditCard,
  'Wallet': Wallet,
  'PiggyBank': PiggyBank,
  'TrendingUp': TrendingUp,
  'BarChart': BarChart,
  
  // Security
  'Lock': Lock,
  'Key': Key,
  
  // Misc
  'Star': Star,
  'Gift': Gift,
  'PartyPopper': PartyPopper,
  'Sparkles': Sparkles,
  'CircleDot': CircleDot,
  'Layers': Layers,
};

// Map icon names to emoji representations
export const iconEmojiMap = {
  // Common categories
  'Package': '📦',
  'Home': '🏠',
  'Car': '🚗',
  'Smartphone': '📱',
  'Shirt': '👕',
  'Sofa': '🛋️',
  'Wrench': '🔧',
  'Briefcase': '💼',
  'Anchor': '⚓',
  'Trees': '🌲',
  'Waves': '🌊',
  'Shield': '🛡️',
  'Map': '🗺️',
  'Crown': '👑',
  'Zap': '⚡',
  'Droplets': '💧',
  'HardHat': '👷',
  'CarFront': '🚙',
  'CarTaxiFront': '🚕',
  'PaintBucket': '🎨',
  
  // Buildings & Places
  'Building2': '🏢',
  'Building': '🏛️',
  'Store': '🏪',
  'Factory': '🏭',
  'Warehouse': '🏚️',
  'School': '🏫',
  'Church': '⛪',
  'Hospital': '🏥',
  
  // Vehicles & Transport
  'Bike': '🚲',
  'Bus': '🚌',
  'Truck': '🚚',
  'Tractor': '🚜',
  'Train': '🚂',
  'Plane': '✈️',
  'PlaneTakeoff': '🛫',
  'Sailboat': '⛵',
  'Ship': '🚢',
  
  // Electronics & Tech
  'Monitor': '🖥️',
  'Tv': '📺',
  'Laptop': '💻',
  'Printer': '🖨️',
  'Router': '📡',
  'Cpu': '🔲',
  'HardDrive': '💾',
  'Database': '🗄️',
  'Wifi': '📶',
  'Headphones': '🎧',
  'Gamepad2': '🎮',
  
  // Fashion & Accessories
  'Watch': '⌚',
  'Glasses': '👓',
  'Gem': '💎',
  'ShoppingBag': '🛍️',
  
  // People & Family
  'Baby': '👶',
  'Users': '👥',
  'UserPlus': '👤',
  'UserCheck': '✅',
  
  // Sports & Fitness
  'Dumbbell': '💪',
  
  // Arts & Entertainment
  'Palette': '🎨',
  'Music': '🎵',
  'Camera': '📷',
  'Paintbrush': '🖌️',
  
  // Education
  'BookOpen': '📚',
  'GraduationCap': '🎓',
  
  // Animals
  'Dog': '🐕',
  'Cat': '🐈',
  'Bird': '🐦',
  
  // Nature & Outdoors
  'Leaf': '🌿',
  'Flower2': '🌸',
  'Mountain': '⛰️',
  'Tent': '⛺',
  'Compass': '🧭',
  'Sun': '☀️',
  'Moon': '🌙',
  'Cloud': '☁️',
  'Umbrella': '☂️',
  'Wind': '💨',
  
  // Food & Drink
  'Utensils': '🍴',
  'Coffee': '☕',
  'Wine': '🍷',
  'Pizza': '🍕',
  'Cake': '🎂',
  
  // Tools & Work
  'Hammer': '🔨',
  'Scissors': '✂️',
  'Tool': '🛠️',
  'Cog': '⚙️',
  'Settings': '⚙️',
  
  // Health
  'Stethoscope': '🩺',
  'Heart': '❤️',
  'Thermometer': '🌡️',
  
  // Communication
  'Phone': '📞',
  'Mail': '📧',
  'MessageSquare': '💬',
  'Video': '🎬',
  
  // Files & Documents
  'Image': '🖼️',
  'FileText': '📄',
  'Folder': '📁',
  
  // Finance
  'CreditCard': '💳',
  'Wallet': '👛',
  'PiggyBank': '🐷',
  'TrendingUp': '📈',
  'BarChart': '📊',
  
  // Security
  'Lock': '🔒',
  'Key': '🔑',
  
  // Misc
  'Star': '⭐',
  'Gift': '🎁',
  'PartyPopper': '🎉',
  'Sparkles': '✨',
  'CircleDot': '⭕',
  'Layers': '📚',
};

/**
 * Get the Lucide React icon component for an icon name
 * @param {string} iconName - The icon name from the database
 * @returns {React.Component} - The Lucide icon component, or Package as default
 */
export const getIconComponent = (iconName) => {
  return iconComponentMap[iconName] || Package;
};

/**
 * Get the emoji representation for an icon name
 * @param {string} iconName - The icon name from the database
 * @returns {string} - The emoji, or 📦 as default
 */
export const getIconEmoji = (iconName) => {
  return iconEmojiMap[iconName] || '📦';
};

/**
 * Get all available icons for the admin icon selector
 * @returns {Array<{name: string, emoji: string}>} - List of available icons
 */
export const getAvailableIcons = () => {
  return Object.entries(iconEmojiMap).map(([name, emoji]) => ({
    name,
    emoji,
    label: name.replace(/([A-Z])/g, ' $1').trim() // Convert CamelCase to spaced words
  }));
};

/**
 * Subcategory to Icon Mapping
 * Maps subcategory names to Lucide icon components for clean UI display
 */
export const subcategoryIconMap = {
  // Real Estate
  'House': Home,
  'Houses': Home,
  'Villa': Building2,
  'Apartments': Building,
  'Land': MapPin,
  'Monument': Building2,
  'Rooms for rent': BedDouble,
  'Property Wanted': Search,
  'Commercial Property': Store,
  'Commercial': Store,
  'Vacation Rental': Plane,
  
  // Vehicles
  'Cars': Car,
  'Motorcycles': Bike,
  'Motorcycles/ Scooters': Bike,
  'Boats': Sailboat,
  'Speedboat': Ship,
  'Speedboats': Ship,
  'Trucks / pickups': Truck,
  'Vans / Buses': Bus,
  'RVs / Camper': Truck,
  'Vehicle Parts / Accessories': Cog,
  'Car Rentals / Leasing': Key,
  'Parts': Cog,
  
  // Electronics
  'Phones': Smartphone,
  'Computers': Laptop,
  'TVs': Tv,
  'Audio': Headphones,
  'Gaming': Gamepad2,
  'Cameras': Camera,
  'Tablets': Smartphone,
  
  // Fashion
  'Men': Shirt,
  'Women': ShoppingBag,
  'Kids': Baby,
  'Accessories': Watch,
  'Shoes': ShoppingBag,
  'Jewelry': Gem,
  
  // Furniture
  'Living Room': Sofa,
  'Bedroom': BedDouble,
  'Office': Briefcase,
  'Outdoor': TreePine,
  'Kitchen': UtensilsCrossed,
  'Dining': UtensilsCrossed,
  
  // Services
  'Home Services': Wrench,
  'Professional': Briefcase,
  'Events': PartyPopper,
  'Lessons': BookOpen,
  'Cleaning': Sparkles,
  'Moving': Truck,
  'Beauty': Sparkles,
  
  // Jobs
  'Full-time': Clock,
  'Part-time': Clock,
  'Freelance': Laptop,
  'Internships': GraduationCap,
  
  // Outdoor & Sports
  'Camping': Tent,
  'Hiking': Mountain,
  'Garden': Flower2,
  'Sports Equipment': Dumbbell,
  'Fitness': Dumbbell,
  'Cycling': Bike,
  'Golf': CircleDot,
  
  // Watersport
  'Surfing': Waves,
  'Diving': Waves,
  'Kayaking': Sailboat,
  'Jet Ski': Waves,
  'Jetski': Waves,
  'Jetboat': Ship,
  'Jet boat': Ship,
  'Fishing': Anchor,
  'Sailing': Sailboat,
  'Sail boat': Sailboat,
  'Sailboat': Sailboat,
  'Yacht': Ship,
  'Powerboat': Ship,
  'other boat': Sailboat,
  'Other boat': Sailboat,
  'Snorkeling': Waves,
  'Kitesurfing': Wind,
  
  // Pets
  'Dogs': Dog,
  'Cats': Cat,
  'Fish': Fish,
  'Birds': Bird,
  'Pet Supplies': ShoppingCart,
  
  // Baby & Kids
  'Baby Gear': Baby,
  'Toys': Gift,
  'Strollers': Baby,
  
  // Art & Collectibles
  'Art': Palette,
  'Antiques': Crown,
  'Collectibles': Star,
  
  // Music
  'Instruments': Music,
  'Guitars': Music,
  'Pianos': Music,
  
  // Default
  'Other': Package,
};

/**
 * Get the Lucide React icon component for a subcategory
 * @param {string} subcategory - The subcategory name
 * @returns {React.Component} - The Lucide icon component, or Package as default
 */
export const getSubcategoryIcon = (subcategory) => {
  return subcategoryIconMap[subcategory] || Package;
};

export default {
  getIconComponent,
  getIconEmoji,
  getAvailableIcons,
  getSubcategoryIcon,
  iconComponentMap,
  iconEmojiMap,
  subcategoryIconMap,
};
